import React from "react";
import { UseComboboxStateChange } from "downshift"; // eslint-disable-line import/no-extraneous-dependencies

import { ComposableSearchDropdown, ComposableSearchDropdownProps } from "@themuse/design-system";

import { IndexedObject } from "@themuse/design-system/lib/components/Dropdowns/ComposableDropdown/DropdownProvider";
import { jobSearchQueryMaxlength } from "utils/constants";
import { SearchKeyword } from "interfaces/context/searchContextInterfaces";

import homeStyles from "styles/Home.module.scss";
import styles from "./SearchInput.module.scss";

interface KeywordSearchFieldProps {
    clear?: boolean;
    onClear?: () => void;
    keyword: SearchKeyword;
    noResultsMessage: React.ReactNode;
    onInputChange: (changes: UseComboboxStateChange<IndexedObject>) => void;
}

const menuToggle = (value: boolean) => {
    const navHeader = document.querySelectorAll("header[class^='Header_header']");

    navHeader.forEach((elem) => {
        if (value) {
            elem.classList.add(homeStyles.inputMenuOpen);
        } else {
            elem.classList.remove(homeStyles.inputMenuOpen);
        }
    });
};

const filterList = (list: IndexedObject[], value = "") => list.filter(
    (item: IndexedObject) => item.label.toLowerCase().includes(value.toLowerCase())
);

const KeywordSearchInput = (props: KeywordSearchFieldProps) => {
    const {
        keyword, clear, onInputChange, noResultsMessage
    } = props;
    const { userSelectedKeyword, userInputValue } = keyword;

    const keywordList = [
        { label: "Marketing", value: "marketing" },
        { label: "Sales", value: "sales" },
        { label: "Product Management", value: "product manager" },
        { label: "Design", value: "design" },
        { label: "Software Engineering", value: "software engineering" },
        { label: "Human Resources", value: "human resources" },
        { label: "Finance", value: "finance" },
        { label: "Project Management", value: "project management" },
        { label: "Customer Care", value: "customer care" },
        { label: "Data Entry", value: "data entry" },
        { label: "Account Manager", value: "account manager" },
        { label: "Accountant", value: "accountant" },
        { label: "Accounting", value: "accounting" },
        { label: "Actuary", value: "actuary" },
        { label: "Admin", value: "admin" },
        { label: "Administrative", value: "administrative" },
        { label: "Administrative Assistant", value: "administrative assistant" },
        { label: "Analyst", value: "analyst" },
        { label: "Analytics", value: "analytics" },
        { label: "Art", value: "art" },
        { label: "Art Director", value: "art director" },
        { label: "Assistant", value: "assistant" },
        { label: "Associate Consultant", value: "associate consultant" },
        { label: "Brand Manager", value: "brand manager" },
        { label: "Brand Marketing", value: "brand marketing" },
        { label: "Business", value: "business" },
        { label: "Business Analyst", value: "business analyst" },
        { label: "Business Development", value: "business development" },
        { label: "Chief Of Staff", value: "chief of staff" },
        { label: "Clinical Educator", value: "clinical educator" },
        { label: "Coach", value: "coach" },
        { label: "Communication", value: "communication" },
        { label: "Communications", value: "communications" },
        { label: "Community Health Worker", value: "community health worker" },
        { label: "Community Manager", value: "community manager" },
        { label: "Compensation And Benefits Manager", value: "compensation and benefits manager" },
        { label: "Compliance", value: "compliance" },
        { label: "Computer Programmer", value: "computer programmer" },
        { label: "Content", value: "content" },
        { label: "Content Marketing", value: "content marketing" },
        { label: "Content Marketing Manager", value: "content marketing manager" },
        { label: "Content Writer", value: "content writer" },
        { label: "Coordinator", value: "coordinator" },
        { label: "Copy Editor", value: "copy editor" },
        { label: "Copywriter", value: "copywriter" },
        { label: "Corporate Communications", value: "corporate communications" },
        { label: "Creative", value: "creative" },
        { label: "Creative Director", value: "creative director" },
        { label: "Curriculum Developer", value: "curriculum developer" },
        { label: "Customer", value: "customer" },
        { label: "Customer Service", value: "customer service" },
        { label: "Customer Service Representative", value: "customer service representative" },
        { label: "Customer Success", value: "customer success" },
        { label: "Customer Success Manager", value: "customer success manager" },
        { label: "Customer Support", value: "customer support" },
        { label: "Data", value: "data" },
        { label: "Data Analyst", value: "data analyst" },
        { label: "Data Entry Clerk", value: "data entry clerk" },
        { label: "Data Intern", value: "data intern" },
        { label: "Data Management Analyst", value: "data management analyst" },
        { label: "Data Science", value: "data science" },
        { label: "Data Science Manager", value: "data science manager" },
        { label: "Data Scientist", value: "data scientist" },
        { label: "Database Administrator", value: "database administrator" },
        { label: "Digital Marketing", value: "digital marketing" },
        { label: "Director", value: "director" },
        { label: "Diversity Manager", value: "diversity manager" },
        { label: "Editing", value: "editing" },
        { label: "Editor", value: "editor" },
        { label: "Editorial Assistant", value: "editorial assistant" },
        { label: "Education", value: "education" },
        { label: "Engineering", value: "engineering" },
        { label: "English Teacher", value: "english teacher" },
        { label: "Enterprise Sales Executive", value: "enterprise sales executive" },
        { label: "Entry Level", value: "entry level" },
        { label: "Environmental Restoration Planner", value: "environmental restoration planner" },
        { label: "Event", value: "event" },
        { label: "Executive", value: "executive" },
        { label: "Executive Assistant", value: "executive assistant" },
        { label: "Fashion", value: "fashion" },
        { label: "Financial Analyst", value: "financial analyst" },
        { label: "Financial Manager", value: "financial manager" },
        { label: "Graphic Design", value: "graphic design" },
        { label: "Graphic Designer", value: "graphic designer" },
        { label: "Health", value: "health" },
        { label: "Healthcare", value: "healthcare" },
        { label: "HR", value: "hr" },
        { label: "HR Associate", value: "hr associate" },
        { label: "HR Manager", value: "hr manager" },
        { label: "HRIS Manager", value: "hris manager" },
        { label: "Industrial Engineer", value: "industrial engineer" },
        { label: "Information Security Analyst", value: "information security analyst" },
        { label: "Information Technology", value: "information technology" },
        { label: "Insurance Sales Agent", value: "insurance sales agent" },
        { label: "Intern", value: "intern" },
        { label: "Internal Auditing Manager", value: "internal auditing manager" },
        { label: "Internship", value: "internship" },
        { label: "Internships", value: "internships" },
        { label: "Interpreter", value: "interpreter" },
        { label: "Interview", value: "interview" },
        { label: "Learning", value: "learning" },
        { label: "Learning And Development", value: "learning and development" },
        { label: "Learning And Development Specialist", value: "learning and development specialist" },
        { label: "Legal", value: "legal" },
        { label: "Loan Officer", value: "loan officer" },
        { label: "Management Consultant", value: "management consultant" },
        { label: "Manager", value: "manager" },
        { label: "Marketing Analyst", value: "marketing analyst" },
        { label: "Marketing Consultant", value: "marketing consultant" },
        { label: "Marketing Manager", value: "marketing manager" },
        { label: "Marketing Specialist", value: "marketing specialist" },
        { label: "Mechanical Engineer", value: "mechanical engineer" },
        { label: "Medical Device Sales", value: "medical device sales" },
        { label: "Medical Writer", value: "medical writer" },
        { label: "Mental Health", value: "mental health" },
        { label: "Nonprofit", value: "nonprofit" },
        { label: "Nonprofits", value: "nonprofits" },
        { label: "Operations", value: "operations" },
        { label: "Operations Research Analyst", value: "operations research analyst" },
        { label: "Paralegal", value: "paralegal" },
        { label: "Part Time", value: "part time" },
        { label: "Pharmaceutical Sales", value: "pharmaceutical sales" },
        { label: "Pharmacist", value: "pharmacist" },
        { label: "Pharmacy Technician", value: "pharmacy technician" },
        { label: "Physician Assistant", value: "physician assistant" },
        { label: "Policy Analyst", value: "policy analyst" },
        { label: "PR", value: "pr" },
        { label: "PR Manager", value: "pr manager" },
        { label: "Procurement", value: "procurement" },
        { label: "Product", value: "product" },
        { label: "Product Manager", value: "product manager" },
        { label: "Product Marketing", value: "product marketing" },
        { label: "Program Manager", value: "program manager" },
        { label: "Project", value: "project" },
        { label: "Project Manager", value: "project manager" },
        { label: "Proofreader", value: "proofreader" },
        { label: "Public Relations", value: "public relations" },
        { label: "Python", value: "python" },
        { label: "Real Estate", value: "real estate" },
        { label: "Recruiter", value: "recruiter" },
        { label: "Registered Nurse", value: "registered nurse" },
        { label: "Regulatory Affairs Manager", value: "regulatory affairs manager" },
        { label: "Remote", value: "remote" },
        { label: "Research", value: "research" },
        { label: "Research Analyst", value: "research analyst" },
        { label: "Sales Consultant", value: "sales consultant" },
        { label: "Sales Engineer", value: "sales engineer" },
        { label: "Sales Representative", value: "sales representative" },
        { label: "Salesforce", value: "salesforce" },
        { label: "School Counselor", value: "school counselor" },
        { label: "Scrum Master", value: "scrum master" },
        { label: "Security", value: "security" },
        { label: "Senior Product Management Director", value: "senior product management director" },
        { label: "Senior UX Designer", value: "senior ux designer" },
        { label: "SEO", value: "seo" },
        { label: "Social Media", value: "social media" },
        { label: "Social Media Coordinator", value: "social media coordinator" },
        { label: "Social Media Manager", value: "social media manager" },
        { label: "Social Worker", value: "social worker" },
        { label: "Software", value: "software" },
        { label: "Software Developer", value: "software developer" },
        { label: "Software Engineer", value: "software engineer" },
        { label: "Software Sales Executive", value: "software sales executive" },
        { label: "Solutions Architect", value: "solutions architect" },
        { label: "Strategy", value: "strategy" },
        { label: "Supply Chain", value: "supply chain" },
        { label: "Sustainability", value: "sustainability" },
        { label: "Teacher", value: "teacher" },
        { label: "Teaching Assistant", value: "teaching assistant" },
        { label: "Technical Writer", value: "technical writer" },
        { label: "Training", value: "training" },
        { label: "Translator", value: "translator" },
        { label: "Tutor", value: "tutor" },
        { label: "User Experience", value: "user experience" },
        { label: "UX", value: "ux" },
        { label: "UX Design", value: "ux design" },
        { label: "UX Designer", value: "ux designer" },
        { label: "UX Researcher", value: "ux researcher" },
        { label: "Video", value: "video" },
        { label: "Virtual Assistant", value: "virtual assistant" },
        { label: "Web Developer", value: "web developer" },
        { label: "Work From Home", value: "work from home" },
        { label: "Writer", value: "writer" },
        { label: "Writing", value: "writing" },
    ];

    const dropdownProps: ComposableSearchDropdownProps = {
        classes: {
            container: styles.searchInputContainer,
            inputIcon: styles.inputIcon
        },
        dropdownProps: {
            dropdownList: filterList(keywordList, userInputValue),
            dropdownTitle: "Type to search for a job",
            onInputChange,
            inputId: "keyword-input",
            onMenuToggle: menuToggle,
            labelText: "Keyword",
            maxLength: jobSearchQueryMaxlength,
            clearValue: clear,
            fieldValue: userSelectedKeyword,
            noResultsMessage,
            placeHolderText: "Job title, keywords",
        }
    };

    return <ComposableSearchDropdown {...dropdownProps} />;
};

KeywordSearchInput.displayName = "KeywordSearchInput";
export default KeywordSearchInput;
